import { useEffect, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { ensureAnalytics, identifyUser, registerContext, resetAnalytics, trackEvent } from '@/lib/analytics';
import { getDubClickId } from '@/lib/dub';

/**
 * Tracks Clerk authentication events to Google Analytics via GTM
 * Fires sign_up event for new users and sign_in event for returning users
 */
export function ClerkAnalyticsTracker() {
  const { isLoaded, isSignedIn, user } = useUser();
  const hasTrackedRef = useRef(false);
  const previousUserIdRef = useRef<string | null>(null);
  const hasTrackedPrdAuthRef = useRef(false);

  useEffect(() => {
    // Attempt to start analytics as soon as Clerk is available (will wait for CMP if needed)
    ensureAnalytics();
  }, [isLoaded]);

  useEffect(() => {
    // Only track once Clerk is loaded and we have a user
    if (!isLoaded || !isSignedIn || !user) {
      return;
    }

    // Prevent duplicate tracking
    if (hasTrackedRef.current && previousUserIdRef.current === user.id) {
      return;
    }

    // Track sign_up or sign_in based on account age
    const accountCreatedAt = user.createdAt ? new Date(user.createdAt).getTime() : 0;
    const now = Date.now();
    const accountAgeMinutes = (now - accountCreatedAt) / (1000 * 60);

    // If account is less than 5 minutes old, consider it a sign_up
    const isNewUser = accountAgeMinutes < 5;

    // Push event to dataLayer for GTM
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      const eventName = isNewUser ? 'sign_up' : 'sign_in';

      (window as any).dataLayer.push({
        event: eventName,
        user_id: user.id,
        user_email: user.primaryEmailAddress?.emailAddress,
        method: 'clerk',
        timestamp: new Date().toISOString()
      });

      console.log(`[Analytics] Tracked ${eventName} event for user:`, user.id);
    }

    hasTrackedRef.current = true;
    previousUserIdRef.current = user.id;

    // Identify in PostHog and send PRD-specific auth events when on the PRD page
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const isPrdPage = pathname === '/prd-generator';
    const plan =
      (user.publicMetadata as Record<string, unknown> | undefined)?.plan ||
      (user.publicMetadata as Record<string, unknown> | undefined)?.tier ||
      (user.unsafeMetadata as Record<string, unknown> | undefined)?.plan ||
      'unknown';

    ensureAnalytics();
    identifyUser(user.id, {
      email: user.primaryEmailAddress?.emailAddress,
      name: user.fullName || user.username,
      plan,
    });
    registerContext({ signed_in: true, plan });

    if (isPrdPage && !hasTrackedPrdAuthRef.current) {
      hasTrackedPrdAuthRef.current = true;
      const eventName = isNewUser ? 'prd_sign_up' : 'prd_sign_in';
      const dubId = getDubClickId();
      trackEvent(eventName, {
        page: pathname,
        method: 'clerk',
        is_new: isNewUser,
        plan,
        user_email: user.primaryEmailAddress?.emailAddress,
        referrer: typeof document !== 'undefined' ? document.referrer : null,
        ...(dubId ? { dub_id: dubId } : {}),
      });
    }
  }, [isLoaded, isSignedIn, user]);

  // Track sign_out
  useEffect(() => {
    if (isLoaded && !isSignedIn && hasTrackedRef.current) {
      // User just signed out
      if (typeof window !== 'undefined' && (window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: 'sign_out',
          timestamp: new Date().toISOString()
        });

        console.log('[Analytics] Tracked sign_out event');
      }

      // Clear PostHog identity/context so anonymous activity isn't tied to the previous user
      resetAnalytics();
      registerContext({ signed_in: false, plan: undefined });

      hasTrackedRef.current = false;
      previousUserIdRef.current = null;
    }
  }, [isLoaded, isSignedIn]);

  return null;
}
